import React, { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { ActivityResponse } from '@troia-v3/types';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useUpdateActivity, useDeleteActivity } from '../hooks/useLeadActivities';
import { StatusPill, getStatusVariant } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons/types';
/**
 * Props do LeadTimeline
 */
export interface LeadTimelineProps {
  /** Lista de atividades para exibir */
  activities: ActivityResponse[];
  /** Estado de loading */
  loading?: boolean;
  /** Callback executado quando atividade é modificada */
  onActivityComplete?: () => void;
}

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendente' },
  { value: 'completed', label: 'Concluída' },
  { value: 'in_progress', label: 'Em andamento' },
  { value: 'cancelled', label: 'Cancelada' },
  { value: 'failed', label: 'Falha' },
] as const;

// Rótulos pt-BR do status — cor/variant vem do STATUS_SEMANTICS canônico
// via getStatusVariant (F63: fim das cores hex hardcoded no badge).
const STATUS_LABELS: Record<string, string> = {
  completed: 'Concluída',
  pending: 'Pendente',
  in_progress: 'Em andamento',
  cancelled: 'Cancelada',
  failed: 'Falha',
};

/**
 * Item individual da timeline com edição inline e exclusão
 */
const TimelineItem: React.FC<{
  activity: ActivityResponse;
  isLast: boolean;
  onActivityComplete?: () => void;
}> = ({ activity, isLast, onActivityComplete }) => {
  const queryClient = useQueryClient();

  // Estado de edição
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    title: activity.title || '',
    description: activity.description || '',
    status: activity.status || 'pending',
  });

  // Estado de exclusão
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Mutations
  const updateMutation = useUpdateActivity();
  const deleteMutation = useDeleteActivity();

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return 'Data inválida';
    }
  };

  const handleStartEdit = () => {
    setEditData({
      title: activity.title || '',
      description: activity.description || '',
      status: activity.status || 'pending',
    });
    setIsEditing(true);
    setShowDeleteConfirm(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSaveEdit = () => {
    if (!editData.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }
    if (!editData.description.trim()) {
      toast.error('Descrição é obrigatória');
      return;
    }

    updateMutation.mutate(
      {
        id: activity.id,
        data: {
          title: editData.title.trim(),
          description: editData.description.trim(),
          status: editData.status as 'completed' | 'in_progress' | 'failed' | 'cancelled' | 'pending',
        },
      },
      {
        onSuccess: () => {
          toast.success('Atividade atualizada com sucesso!');
          setIsEditing(false);
          onActivityComplete?.();
        },
        onError: (error: unknown) => {
          const err = error as { response?: { data?: { message?: string } } };
          toast.error(err.response?.data?.message || 'Erro ao atualizar atividade');
        },
      }
    );
  };

  const handleCompleteActivity = () => {
    updateMutation.mutate(
      { id: activity.id, data: { status: 'completed' } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['lead-activities'] });
          queryClient.invalidateQueries({ queryKey: ['activities'] });
          queryClient.invalidateQueries({ queryKey: ['my-activities'] });
          toast.success('Atividade concluída com sucesso!');
          onActivityComplete?.();
        },
        onError: (error: unknown) => {
          const err = error as { response?: { data?: { message?: string } } };
          toast.error(err.response?.data?.message || 'Erro ao concluir atividade');
        },
      }
    );
  };

  const handleDelete = () => {
    deleteMutation.mutate(activity.id, {
      onSuccess: () => {
        toast.success('Atividade excluída com sucesso!');
        setShowDeleteConfirm(false);
        onActivityComplete?.();
      },
      onError: (error: unknown) => {
        const err = error as { response?: { data?: { message?: string } } };
        toast.error(err.response?.data?.message || 'Erro ao excluir atividade');
      },
    });
  };

  const status = activity.status || 'pending';
  const statusLabel = STATUS_LABELS[status] ?? status;

  // Atividades geradas pelo sistema (transferência, atribuição automática,
  // routing rule, agente IA, workflow) são imutáveis — nem o operador que
  // disparou pode editar ou excluir o registro de auditoria. Apenas
  // atividades manuais (`actorType === 'user'`) e do tipo `communication`
  // permanecem editáveis pra preservar o caso de uso original (lembrete,
  // anotação livre, etc.).
  const isSystemEntry = activity.actorType !== 'user' || activity.type === 'assignment';

  // Ícone representativo do tipo de evento — aplicado ao "avatar" quando o
  // registro é do sistema (não tem actor humano com foto/iniciais).
  const systemIcon: IconName = activity.type === 'assignment'
    ? 'user-check'
    : activity.type === 'status_change'
      ? 'refresh'
      : activity.type === 'workflow'
        ? 'zap'
        : 'settings';

  // Iniciais do actor pro `.tl-dot` (26px, slate) — 2 letras como no mockup
  const actorInitials = [activity.actor?.firstName, activity.actor?.lastName]
    .filter(Boolean)
    .map((w) => String(w).charAt(0).toUpperCase())
    .slice(0, 2)
    .join('') || 'U';

  return (
    /* `.tl-item` do mockup: padding 0 0 18px 34px; trilha 2px em left 12 */
    <div className="relative group" style={{ padding: '0 0 18px 34px' }}>
      {!isLast && (
        <div
          className="absolute"
          style={{ left: 12, top: 26, bottom: -2, width: 2, backgroundColor: '#eef1f5' }}
        />
      )}

      {/* `.tl-dot`: 26×26 radius 32%; author = slate com iniciais, sistema = cinza com ícone */}
      {isSystemEntry ? (
        <div
          className="absolute flex items-center justify-center"
          style={{ left: 0, top: 1, width: 26, height: 26, borderRadius: '32%', backgroundColor: '#94a3b8', color: '#fff' }}
          data-testid={`lead-timeline-system-icon-${activity.id}`}
        >
          <Icon name={systemIcon} size={14} />
        </div>
      ) : activity.actor?.picture ? (
        <img
          src={activity.actor.picture}
          alt={activity.actor?.firstName || 'Usuário'}
          className="absolute object-cover"
          style={{ left: 0, top: 1, width: 26, height: 26, borderRadius: '32%' }}
        />
      ) : (
        <div
          className="absolute flex items-center justify-center text-[10px] font-semibold text-white"
          style={{ left: 0, top: 1, width: 26, height: 26, borderRadius: '32%', backgroundColor: '#64748b' }}
        >
          {actorInitials}
        </div>
      )}

      {/* Content */}
      <div className="min-w-0">
          {isEditing ? (
            /* === MODO EDIÇÃO INLINE === */
            <div className="space-y-3">
              {/* Título editável */}
              <input
                type="text"
                value={editData.title}
                onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm font-medium rounded-lg border h-[38px]"
                style={{
                  backgroundColor: '#ffffff',
                  borderColor: '#d1d5db',
                  color: '#1e293b',
                }}
                placeholder="Título da atividade"
                maxLength={200}
              />

              {/* Descrição editável */}
              <textarea
                value={editData.description}
                onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm rounded-lg border resize-none"
                style={{
                  backgroundColor: '#ffffff',
                  borderColor: '#d1d5db',
                  color: '#1e293b',
                }}
                placeholder="Descrição da atividade"
                rows={2}
              />

              {/* Status selecionável */}
              <div className="flex items-center gap-3">
                <label className="text-xs font-medium" style={{ color: '#64748b' }}>
                  Status:
                </label>
                <select
                  value={editData.status}
                  onChange={(e) => setEditData(prev => ({ ...prev, status: e.target.value as typeof prev.status }))}
                  className="px-2 py-1 text-xs rounded-lg border h-[38px]"
                  style={{
                    backgroundColor: '#ffffff',
                    borderColor: '#d1d5db',
                    color: '#1e293b',
                  }}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Botões Salvar / Cancelar */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSaveEdit}
                  disabled={updateMutation.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                  style={{ backgroundColor: '#8b5cf6', color: '#ffffff' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#7c3aed'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#8b5cf6'; }}
                >
                  {updateMutation.isPending ? (
                    <>
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Icon name="save" className="w-3 h-3" />
                      Salvar
                    </>
                  )}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={updateMutation.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                  style={{ backgroundColor: '#F3F4F6', color: '#4B5563' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E5E7EB'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6'; }}
                >
                  <Icon name="x" className="w-3 h-3" />
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            /* === MODO VISUALIZAÇÃO === */
            <>
              {/* `.tl-head`: título 14 semibold + badge; ações reveladas no hover */}
              <div className="flex items-start gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                  <h4 className="text-sm font-semibold" style={{ color: '#1e293b' }}>
                    {activity.title}
                    {/* ação em parênteses só quando difere do título (mockup mostra só o título) */}
                    {activity.action && activity.action.trim() !== activity.title.trim() && (
                      <span className="font-normal" style={{ color: '#64748b' }}> ({activity.action})</span>
                    )}
                  </h4>
                  <StatusPill
                    variant={getStatusVariant(status)}
                    label={statusLabel}
                    data-testid={`lead-timeline-status-${activity.id}`}
                  />
                </div>

                {/* `.tl-actions` (opacity 0 → hover): Concluir verde + editar + excluir.
                    Ocultas para registros de sistema (auditoria imutável). */}
                {!isSystemEntry && (
                  <div className="flex items-center gap-[3px] ml-auto flex-none opacity-0 group-hover:opacity-100 transition-opacity">
                    {activity.status === 'pending' && (
                      <button
                        onClick={handleCompleteActivity}
                        disabled={updateMutation.isPending}
                        className="inline-flex items-center gap-[5px] text-xs font-semibold text-white whitespace-nowrap rounded-[7px] disabled:opacity-50"
                        style={{ height: 26, padding: '0 9px', backgroundColor: '#10b981' }}
                        onMouseEnter={(e) => { if (!updateMutation.isPending) e.currentTarget.style.backgroundColor = '#059669'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#10b981'; }}
                        data-testid={`lead-timeline-complete-${activity.id}`}
                      >
                        <Icon name="check" size={14} />
                        {updateMutation.isPending ? 'Concluindo…' : 'Concluir'}
                      </button>
                    )}
                    <button
                      onClick={handleStartEdit}
                      className="w-[26px] h-[26px] rounded-[7px] flex items-center justify-center transition-colors"
                      style={{ color: '#94a3b8' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#475569'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
                      title="Editar atividade"
                      data-testid={`lead-timeline-edit-${activity.id}`}
                    >
                      <Icon name="edit" size={14} />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-[26px] h-[26px] rounded-[7px] flex items-center justify-center transition-colors"
                      style={{ color: '#94a3b8' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
                      title="Excluir atividade"
                      data-testid={`lead-timeline-delete-${activity.id}`}
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </div>
                )}
              </div>

              {/* `.tl-date`: 12px #94a3b8 */}
              <div className="text-xs" style={{ color: '#94a3b8', margin: '2px 0' }}>
                {formatDate(activity.occurredAt)}
              </div>

              {/* Confirmação de Exclusão Inline */}
              {showDeleteConfirm && (
                <div
                  className="flex items-center gap-2 px-3 py-2 rounded-lg mb-2"
                  style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}
                >
                  <span className="text-xs font-medium" style={{ color: '#991B1B' }}>
                    Tem certeza que deseja excluir?
                  </span>
                  <button
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                    className="px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50"
                    style={{ backgroundColor: '#DC2626', color: '#ffffff' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#B91C1C'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#DC2626'; }}
                  >
                    {deleteMutation.isPending ? 'Excluindo...' : 'Excluir'}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleteMutation.isPending}
                    className="px-2 py-1 text-xs font-medium rounded transition-colors"
                    style={{ backgroundColor: '#F3F4F6', color: '#4B5563' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E5E7EB'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#F3F4F6'; }}
                  >
                    Cancelar
                  </button>
                </div>
              )}

              {/* `.tl-desc`: 13px #475569 */}
              <p className="text-[13px]" style={{ color: '#475569', whiteSpace: 'pre-line' }}>
                {activity.description}
              </p>

              {/* `.tl-users`: avatares 22px slate sobrepostos (borda branca 2px) */}
              {activity.relatedEntities && activity.relatedEntities.filter(e => e.type === 'user').length > 0 && (
                <div className="flex" style={{ marginTop: 8 }}>
                  {activity.relatedEntities
                    .filter(entity => entity.type === 'user')
                    .slice(0, 5)
                    .map((entity, idx) => (
                      entity.picture ? (
                        <img
                          key={entity.id}
                          src={entity.picture}
                          alt={entity.name || 'Usuário'}
                          className="object-cover"
                          style={{ width: 22, height: 22, borderRadius: '32%', border: '2px solid #fff', marginLeft: idx > 0 ? -6 : 0 }}
                          title={entity.name}
                        />
                      ) : (
                        <span
                          key={entity.id}
                          className="flex items-center justify-center text-[9px] font-semibold text-white"
                          style={{ width: 22, height: 22, borderRadius: '32%', backgroundColor: '#64748b', border: '2px solid #fff', marginLeft: idx > 0 ? -6 : 0 }}
                          title={entity.name}
                        >
                          {entity.name?.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || 'U'}
                        </span>
                      )
                    ))}
                  {activity.relatedEntities.filter(e => e.type === 'user').length > 5 && (
                    <span
                      className="flex items-center justify-center text-[9px] font-semibold"
                      style={{ width: 22, height: 22, borderRadius: '32%', backgroundColor: '#f1f5f9', color: '#64748b', border: '2px solid #fff', marginLeft: -6 }}
                    >
                      +{activity.relatedEntities.filter(e => e.type === 'user').length - 5}
                    </span>
                  )}
                </div>
              )}

              {/* Changes Summary */}
              {activity.summary && (
                <div
                  className="mt-2 px-3 py-2 rounded text-xs"
                  style={{
                    backgroundColor: '#F9FAFB',
                    color: '#64748b',
                    whiteSpace: 'pre-line'
                  }}
                >
                  {activity.summary}
                </div>
              )}
            </>
          )}
      </div>
    </div>
  );
};

/**
 * Componente de Timeline de Atividades do Lead
 *
 * Exibe cronologicamente as atividades relacionadas ao lead
 * com tema always-light para consistência visual.
 */
export const LeadTimeline: React.FC<LeadTimelineProps> = ({
  activities,
  loading = false,
  onActivityComplete
}) => {

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#8b5cf6' }} />
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div className="text-center py-8">
        <Icon name="activity" className="w-12 h-12 mx-auto mb-3 opacity-40" style={{ color: '#64748b' }} />
        <p className="text-sm" style={{ color: '#64748b' }}>
          Nenhuma atividade registrada
        </p>
      </div>
    );
  }

  return (
    <div>
      {activities.map((activity, index) => (
        <TimelineItem
          key={activity.id}
          activity={activity}
          isLast={index === activities.length - 1}
          onActivityComplete={onActivityComplete}
        />
      ))}
    </div>
  );
};
